import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2 } from "lucide-react";
import DateInput from "./DateInput";
import { formatNAS, validateNAS, formatCodePostal, formatTelephone } from "../utils/formatters";
import VilleAutocomplete from "./VilleAutocomplete";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const emptyMR = { nom: "", prenom: "", date_naissance: "", sexe: 1, nas: "", no_bande: "",
  adresse: "", ville: "", province: "QC", code_postal: "",
  telephone_residence: "", telephone_bureau: "" };

// Section Multi-Requérants — CRUD des requérants secondaires d'un dossier DAJ.
// Le requérant principal (dans l'onglet Requérant lui-même) n'est PAS listé ici
// et n'est jamais supprimable — parité legacy MultiRequerant.vb / FrmMultiReq.vb.
export const MultiRequerantsSection = ({ noseqdaj, setMessage }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {id, ...} = edit
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!noseqdaj) { setRows([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/daj/${noseqdaj}/multirequerants`);
      setRows(res.data || []);
    } catch (e) {
      console.warn("Chargement multi-requérants échoué :", e);
    } finally {
      setLoading(false);
    }
  }, [noseqdaj]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!editing || !editing.nom?.trim() || !editing.prenom?.trim()) {
      setMessage && setMessage("Nom et prénom sont obligatoires");
      return;
    }
    if (!noseqdaj) {
      setMessage && setMessage("Enregistrez d'abord le dossier avant d'ajouter un multi-requérant.");
      return;
    }
    // Blocage si NAS saisi mais invalide (algorithme Luhn)
    if (editing.nas && editing.nas.replace(/-/g, '').length > 0) {
      const v = validateNAS(editing.nas);
      if (!v.valid) {
        setMessage && setMessage(`N.A.S. invalide : ${v.message}`);
        setEditing(prev => ({ ...prev, _nasError: v.message }));
        return;
      }
    }
    setSaving(true);
    try {
      // Retire id/noclient/noseqdaj/_nasError du payload
      const { id, noclient, noseqdaj: _seq, _nasError, ...payload } = editing;
      if (id) {
        await axios.put(`${API}/daj/${noseqdaj}/multirequerants/${id}`, payload);
        setMessage && setMessage(`Requérant secondaire ${editing.nom} mis à jour`);
      } else {
        await axios.post(`${API}/daj/${noseqdaj}/multirequerants`, payload);
        setMessage && setMessage(`Requérant secondaire ${editing.nom} ajouté`);
      }
      setEditing(null);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => `${d.loc?.slice(-1)}: ${d.msg}`).join(' | ')
        : (detail || e.message);
      setMessage && setMessage(`Erreur : ${msg}`);
      console.error("MultiReq save error:", e.response?.data || e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Supprimer le requérant secondaire ${row.nom} ${row.prenom} ?`)) return;
    try {
      await axios.delete(`${API}/daj/${noseqdaj}/multirequerants/${row.id}`);
      setMessage && setMessage(`Requérant secondaire supprimé`);
      await load();
    } catch (e) {
      setMessage && setMessage(`Erreur suppression : ${e.response?.data?.detail || e.message}`);
    }
  };

  if (!noseqdaj) {
    return <div style={{ padding: 8, color: "#666", fontStyle: "italic" }}>
      Enregistrez le dossier pour gérer les multi-requérants.
    </div>;
  }

  return (
    <div className="win-multireq-section" data-testid="multireq-section">
      <table className="win-table" style={{ width: "100%", fontSize: 12 }}>
        <thead>
          <tr>
            <th>Nom</th><th>Prénom</th><th>Date de naiss.</th><th>Adresse</th>
            <th>Ville</th><th>N.A.S.</th><th>No. Client</th>
            <th style={{ width: 96 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                <span>Actions</span>
                <button type="button" className="mreq-icon-btn mreq-icon-add" onClick={() => setEditing({ ...emptyMR })} title="Ajouter un requérant secondaire" data-testid="btn-multireq-add">
                  <Plus size={16} />
                </button>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={8} style={{ textAlign: "center", padding: 8 }}>Chargement…</td></tr>}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={8} style={{ textAlign: "center", padding: 8, color: "#666" }}>
              Aucun requérant secondaire. Cliquez sur l'icône « + » dans la colonne Actions pour en créer un.
            </td></tr>
          )}
          {rows.map(r => (
            <tr key={r.id} data-testid={`multireq-row-${r.id}`}>
              <td>{r.nom}</td>
              <td>{r.prenom}</td>
              <td>{r.date_naissance || ""}</td>
              <td>{r.adresse || ""}</td>
              <td>{r.ville || ""}</td>
              <td>{r.nas || ""}</td>
              <td>{r.noclient}</td>
              <td>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button type="button" className="mreq-icon-btn mreq-icon-edit" onClick={() => setEditing(r)} title="Éditer ce requérant" data-testid={`btn-multireq-edit-${r.id}`}>
                    <Pencil size={15} />
                  </button>
                  <button type="button" className="mreq-icon-btn mreq-icon-del" onClick={() => handleDelete(r)} title="Supprimer ce requérant" data-testid={`btn-multireq-del-${r.id}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <div className="modal-overlay" data-testid="multireq-modal">
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ minWidth: 620 }}>
            <div className="modal-header">
              <h3>{editing.id ? "Modifier" : "Ajouter"} un requérant secondaire</h3>
              <button onClick={() => !saving && setEditing(null)} className="modal-close">×</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, padding: 12 }}>
              <label>Nom *<input className="win-input" value={editing.nom || ""} onChange={e => setEditing({ ...editing, nom: e.target.value })} data-testid="mreq-input-nom" /></label>
              <label>Prénom *<input className="win-input" value={editing.prenom || ""} onChange={e => setEditing({ ...editing, prenom: e.target.value })} data-testid="mreq-input-prenom" /></label>
              <label>Date de naissance
                <DateInput
                  value={editing.date_naissance || ""}
                  onChange={v => setEditing(prev => ({ ...prev, date_naissance: v }))}
                  testId="mreq-input-dob"
                />
              </label>
              <label>Sexe
                <select className="win-select" value={editing.sexe ?? 1} onChange={e => setEditing({ ...editing, sexe: parseInt(e.target.value) })} data-testid="mreq-input-sexe">
                  <option value={0}>Féminin</option><option value={1}>Masculin</option><option value={2}>Autre</option>
                </select>
              </label>
              <label>N.A.S.<input
                className="win-input"
                value={editing.nas || ""}
                onChange={e => setEditing({ ...editing, nas: formatNAS(e.target.value) })}
                onBlur={e => {
                  const v = validateNAS(e.target.value);
                  setEditing(prev => ({ ...prev, _nasError: v.valid ? '' : v.message }));
                }}
                maxLength={11}
                placeholder="XXX-XXX-XXX"
                data-testid="mreq-input-nas"
                style={{borderColor: editing._nasError ? '#c00' : undefined}}
              />
                {editing._nasError && (
                  <span style={{fontSize: '10px', color: '#c00', lineHeight: 1.2}} data-testid="mreq-input-nas-error">{editing._nasError}</span>
                )}
              </label>
              <label>No de bande<input className="win-input" value={editing.no_bande || ""} onChange={e => setEditing({ ...editing, no_bande: e.target.value })} /></label>
              <label style={{ gridColumn: "1 / span 2" }}>Adresse<input className="win-input" value={editing.adresse || ""} onChange={e => setEditing({ ...editing, adresse: e.target.value })} /></label>
              <label>Ville<VilleAutocomplete value={editing.ville || ""} onChange={v => setEditing({ ...editing, ville: v })} testId="multireq-ville" /></label>
              <label>Province<input className="win-input" value={editing.province || "QC"} onChange={e => setEditing({ ...editing, province: e.target.value.toUpperCase() })} maxLength={2} /></label>
              <label>Code postal<input
                className="win-input"
                value={editing.code_postal || ""}
                onChange={e => setEditing({ ...editing, code_postal: formatCodePostal(e.target.value) })}
                maxLength={7}
                placeholder="A1A 1A1"
                data-testid="mreq-input-cp"
              /></label>
              <label>Tél. résidence<input
                className="win-input"
                value={editing.telephone_residence || ""}
                onChange={e => setEditing({ ...editing, telephone_residence: formatTelephone(e.target.value) })}
                maxLength={14}
                placeholder="999-999-9999"
              /></label>
              <label>Tél. bureau/cell.<input
                className="win-input"
                value={editing.telephone_bureau || ""}
                onChange={e => setEditing({ ...editing, telephone_bureau: formatTelephone(e.target.value) })}
                maxLength={14}
                placeholder="999-999-9999"
              /></label>
            </div>
            <div className="modal-footer" style={{ display: "flex", gap: 6, padding: 10, justifyContent: "flex-end", borderTop: "1px solid #808080" }}>
              <button className="win-btn" onClick={() => setEditing(null)} disabled={saving} data-testid="btn-multireq-cancel">Annuler</button>
              <button className="win-btn-action" onClick={handleSave} disabled={saving} data-testid="btn-multireq-save">
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiRequerantsSection;
